import { AsyncPipe } from '@angular/common'
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core'
import { FormlyFieldProps } from '@ngx-formly/ng-zorro-antd/form-field'
import { TranslateModule, TranslatePipe } from '@ngx-translate/core'
import { NzFormDirective } from 'ng-zorro-antd/form'
import { MarkdownPipe } from 'ngx-markdown'

import {
  AlertBodyDirective,
  AlertComponent,
  AlertTitleDirective,
} from '../../../components/alert/alert.component'
import { ButtonComponent } from '../../../components/button/button.component'
import { FormlySelectFieldConfig } from '../../../components/formly/types/select-field-type/select-field-type.component'
import { NzFormFieldWrapperConfig } from '../../../components/formly/wrapper/nz-form-field-wrapper/nz-form-field-wrapper.component'
import {
  BRANCH_FILE_TYPES,
  BRANCH_REASONS,
  BRANCH_SOURCES,
  BranchFileType,
} from '../../../models/branch.model'
import { FUNDING_CODES } from '../../../models/data.model'
import {
  FormlyTranslationService,
  provideFormlyTranslation,
} from '../../../services/formly-translation.service'
import { MetaDataModel } from '../upload-file.model'

@Component({
  selector: 'app-step-metadata',
  standalone: true,
  providers: [
    provideFormlyTranslation('pages.uploadFile.steps.metadata.fields'),
  ],
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    TranslatePipe,
    NzFormDirective,
    RouterLink,
    ButtonComponent,
    AlertComponent,
    AlertTitleDirective,
    AlertBodyDirective,
    MarkdownPipe,
  ],
  templateUrl: './step-metadata.component.html',
})
export class StepMetadataComponent implements OnInit {
  @Input() public metaDataModel: MetaDataModel = {}
  @Output() public metaDataModelChange = new EventEmitter<MetaDataModel>()
  @Output() public stepCompleted = new EventEmitter<void>()

  public metaDataForm = new FormGroup({})
  public metaDataFields: FormlyFieldConfig[] = []
  public metaDataTypeConditions: Partial<
    Record<BranchFileType, Partial<Record<keyof MetaDataModel, boolean>>>
  > = {
    hkbw: {
      reason: true,
      source: true,
      note: true,
    },
  }

  public constructor(
    private readonly formlyTranslationService: FormlyTranslationService
  ) {}

  public ngOnInit(): void {
    this.buildForms()
  }

  public metaDataSubmit(): void {
    if (this.metaDataForm.valid) {
      this.metaDataModelChange.emit(this.metaDataModel)
      this.stepCompleted.emit()
    }
  }

  private buildForms(): void {
    this.metaDataFields = [
      {
        fieldGroupClassName: 'grid grid-cols-1 gap-12 lg:grid-cols-[2fr_3fr]',
        fieldGroup: [
          this.setupTypeField(),
          {
            fieldGroup: [
              {
                type: 'placeholder',
                expressions: {
                  hide: (field): boolean => field.parent?.model.type,
                },
              },
              this.setupSourceField(),
              this.setupReasonField(),
              {
                fieldGroupClassName: 'flex gap-6',
                fieldGroup: [
                  this.setupFundingCodeField(),
                  this.setupMevIdField(),
                ],
              },
              this.setupNoteField(),
            ],
          },
        ],
      },
    ]
  }

  private setupNoteField(): NzFormFieldWrapperConfig {
    return {
      key: 'note',
      type: 'input',
      props: {
        label: 'models.branch.note.label',
        placeholder: 'models.branch.note.label',
        showOptionalMarker: true,
        maxLength: 255,
      },
      expressions: {
        hide: this.resolveMetaDataTypeCondition.bind(this).bind(this),
      },
    }
  }

  private setupMevIdField(): NzFormFieldWrapperConfig {
    return {
      key: 'mevId',
      type: 'input',
      className: 'w-1/3',
      props: {
        label: 'models.branch.mevId.label',
        placeholder: 'models.branch.mevId.label',
        showOptionalMarker: true,
        maxLength: 10,
      },
      validators: ['number'],
      expressions: {
        hide: (field) => this.resolveMetaDataTypeCondition(field),
      },
    }
  }

  private setupFundingCodeField(): FormlySelectFieldConfig {
    return {
      key: 'fundingCode',
      type: 'select',
      className: 'w-2/3',
      props: {
        label: 'models.branch.fundingCode.label',
        placeholder: 'models.branch.fundingCode.label',
        required: true,
        allowClear: true,
        options: this.formlyTranslationService.getTranslatedOptions(
          FUNDING_CODES,
          'fundingCode',
          true
        ),
      },
      expressions: {
        hide: (field) => this.resolveMetaDataTypeCondition(field),
      },
    }
  }

  private setupReasonField(): FormlySelectFieldConfig {
    return {
      key: 'reason',
      type: 'select',
      props: {
        label: 'models.branch.reason.label',
        placeholder: 'models.branch.reason.label',
        required: true,
        allowClear: true,
        options: this.formlyTranslationService.getTranslatedOptions(
          BRANCH_REASONS,
          'reason',
          true
        ),
      },
      expressions: {
        hide: (field) => this.resolveMetaDataTypeCondition(field),
      },
    }
  }

  private setupSourceField(): FormlySelectFieldConfig {
    return {
      key: 'source',
      type: 'select',
      props: {
        label: 'models.branch.source.label',
        placeholder: 'models.branch.source.label',
        required: true,
        options: this.formlyTranslationService.getTranslatedOptions(
          BRANCH_SOURCES,
          'source',
          true
        ),
        allowClear: true,
      },
      expressions: {
        hide: (field) => this.resolveMetaDataTypeCondition(field),
      },
    }
  }

  private setupTypeField(): FormlySelectFieldConfig {
    return {
      key: 'type',
      type: 'select',
      props: {
        label: 'models.branch.type.label',
        placeholder: 'models.branch.type.label',
        required: true,
        allowClear: true,
        options: this.formlyTranslationService.getTranslatedOptions(
          BRANCH_FILE_TYPES,
          'type'
        ),
      },
    }
  }

  private resolveMetaDataTypeCondition(
    field: FormlyFieldConfig<FormlyFieldProps>
  ): boolean {
    if (!field.parent?.model.type) return true

    const type = field.parent?.model.type as BranchFileType
    const key = field.key as keyof MetaDataModel

    return !(this.metaDataTypeConditions[type]?.[key] ?? false)
  }
}
